/**
 * The single integration point for the *Show details* affordance.
 *
 * Every consumer (Object Explorer tile, Table Explorer dropdown, schema/data
 * compare pickers, tab-failure banner) calls `showConnectionDiagnostics` rather
 * than reaching for the dialog directly, so the dialog is wired in exactly one
 * place. `ConnectionDiagnosticsDialogHost` — mounted unconditionally in
 * `App.tsx` — registers the real handler in an effect at boot.
 *
 * The pluggable handler is what lets e2e specs assert "clicking *Show details*
 * reached this entry point" without coupling to whichever UI is wired.
 */

export type ShowConnectionDiagnosticsArgs = {
  connectionId: string;
  trigger: 'list_failure' | 'list_empty' | 'pool_open_failure' | 'manual';
  scopeDatabase?: string;
};

type DiagnosticsHandler = (args: ShowConnectionDiagnosticsArgs) => void;

let registeredHandler: DiagnosticsHandler | null = null;

/** `ConnectionDiagnosticsDialogHost` registers the real handler here at boot. */
export function registerConnectionDiagnosticsHandler(handler: DiagnosticsHandler): void {
  registeredHandler = handler;
  // Expose a tiny global hook for E2E specs so they can drive the dialog
  // without coupling to a particular consumer surface (Object Explorer tile,
  // Table Explorer dropdown, future tab-failure banner). Production renderers
  // assigning this at boot is harmless — no PII flows through it.
  if (typeof window !== 'undefined') {
    (window as unknown as {
      __SHOW_CONN_DIAG__?: DiagnosticsHandler;
    }).__SHOW_CONN_DIAG__ = (args) => showConnectionDiagnostics(args);
  }
}

/** Test-only: clear any registered handler. */
export function clearConnectionDiagnosticsHandler(): void {
  registeredHandler = null;
}

/**
 * Invoked by every UI surface that wants to show the connection-diagnostics
 * dialog.
 *
 * The analytics event and the DOM event fire on EVERY request, before the
 * handler runs — they describe the user's action, not which branch served it.
 * They used to live only in the no-handler fallback, so once the dialog host
 * started registering at boot, `connection-diagnostics:show_requested` stopped
 * reaching Umami in the shipped app entirely: the branch carrying it became
 * unreachable in production (#108). The DOM event moved here from the host for
 * the same reason — one dispatch site means e2e specs observe exactly one
 * event whether or not a handler is registered.
 */
export function showConnectionDiagnostics(args: ShowConnectionDiagnosticsArgs): void {
  try {
    window.electronAPI?.analytics
      ?.track('connection-diagnostics:show_requested', { trigger: args.trigger })
      .catch(() => {});
  } catch {
    // window/electronAPI may be unavailable in unit tests — swallow.
  }
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(
      new CustomEvent('connection-diagnostics:show_requested', { detail: args })
    );
  }
  // Absent only in the window between first paint and the host's boot effect,
  // and in unit tests that never mount the host.
  registeredHandler?.(args);
}
